import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../../../api/client';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppDialog } from '../../../components/AppDialog';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import FormLink from '../../../components/FormLink';
import { useNotify } from '../../../components/notifications';
import { useSaveFlow, useConfirm } from '../../../components/confirm';
import { fmtLocalDateTime } from '../../../utils/datetime';
import '../../backlog/backlog.css';
import '../../modules/SettingsDetailPage.css';
// The Reports dropdown's menu chrome (.po-reports-*) already lives here —
// the Ordering Items History list uses the same menu (standing rule 8).
import './requisition.css';
import './PurchaseOrderDetailPage.css';

interface Option { id: string; name: string; }

interface LinkedRequisition {
  oid: string;
  rqid: string | null;
  itemName: string;
  orderQty: number;
  unitPrice: number | null;
  // The rest of the legacy "Pending Item" grid (Purchaseorder_Item_ListView).
  catalog: string | null;
  description: string | null;
  packUnits: string | null;
  expPrice: number | null;
  departmentName: string | null;
  deliveryPriorityName: string | null;
  projectName: string | null;
  /** TaskStatus of the line — 5 PartiallyReceived / 6 Received cannot be unlinked. */
  status: number;
}

/** One Pending Item as the read-only popup shows it. Mirrors the API's
 *  PendingItemDetail, which mirrors legacy's Requisition_DetailView layout. */
interface PendingItemDetail {
  oid: string;
  rqid: string | null;
  itemName: string | null;
  categoryName: string | null;
  vendorName: string | null;
  description: string | null;
  packUnits: string | null;
  itemUnit: number | null;
  catalog: string | null;
  /** Repeats the category — a legacy quirk, see the SP header. */
  nameText: string | null;
  approvedByName: string | null;
  stockQty: number;
  orderQty: number;
  unitPrice: number;
  extPrice: number;
  shippingOption: string | null;
  comment: string | null;
  totalPrice: number;
  deliveryPriorityName: string | null;
  projectName: string | null;
  divisionName: string | null;
}

interface PoForm {
  leadRequisitionId: string;
  shippingOptionId: string;
  // Ship Via is an FK on dbo.Purchaseorder (-> dbo.ShipVia), not free text.
  // It rendered as a text box, so a typed value could never be saved.
  shipViaId: string;
  shippingCharge: string;
  tax: number;
  totalPrice: number;
  expectDate: string;
  referenceNumber: string;
  trackingNumber: string;
  comment: string;
}

const EMPTY: PoForm = {
  leadRequisitionId: '', shippingOptionId: '', shipViaId: '', shippingCharge: '',
  tax: 0, totalPrice: 0, expectDate: '',
  referenceNumber: '', trackingNumber: '', comment: '',
};

/** Server-owned facts shown read-only (legacy "Vendor Account" panel + header). */
interface PoInfo {
  purchaseDate?: string | null;
  purchaseByName?: string | null;
  vendorName?: string | null;
  vendorContact?: string | null;
  vendorAccount?: string | null;
  vendorAddress?: string | null;
  vendorPhone?: string | null;
  vendorEmail?: string | null;
  numItems?: number | null;
}

export default function PurchaseOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';
  const navigate = useNavigate();
  // Ordering Items hands the chosen vendor over here (legacy
  // Tar_CustomProcessSelectedItem, which opens a new PO already carrying that
  // vendor's whole pending-ordering basket).
  const [sp] = useSearchParams();
  const fromVendorId = isNew ? sp.get('vendorId') : null;
  const fromVendorName = isNew ? sp.get('vendor') : null;
  const notify = useNotify();
  const saveFlow = useSaveFlow();
  const { prompt } = useConfirm();

  const [form, setForm] = useState<PoForm>(EMPTY);
  const [poId, setPoId] = useState('');
  // Read-only vendor/header facts the API already returns — legacy's
  // "Vendor Account" panel on Purchaseorder_DetailView.
  const [info, setInfo] = useState<PoInfo>({});
  const [loading, setLoading] = useState(!isNew);
  const [linkedReqs, setLinkedReqs] = useState<LinkedRequisition[]>([]);
  /* The Link picker needs the WHOLE candidate row, not the {id,name} option the
     old combo used — linking has to push a real Pending Item into the grid. */
  const [pendingReqRows, setPendingReqRows] = useState<LinkedRequisition[]>([]);
  const [linkOpen, setLinkOpen]   = useState(false);
  const [linkPick, setLinkPick]   = useState<Set<string | number>>(new Set());
  /* Legacy's "Reports" dropdown, from the same per-view registry the Ordering
     Items History list reads (FormReportPermissionViewController). */
  const [reports, setReports]         = useState<Array<{ reportName: string; caption: string }>>([]);
  const [reportsOpen, setReportsOpen] = useState(false);
  /* Prev/next RECORD arrows (standing rule 12; legacy draws them top-right of
     Purchaseorder_DetailView). The sibling set is the Ordering Items History
     list — the same order it shows — so the arrows walk what the user just
     came from rather than an order invented here. */
  const [siblings, setSiblings] = useState<string[]>([]);
  /* VIEW-then-EDIT, the same contract the shared <SettingsDetailPage> already
     uses for every list→detail form (`initialMode: 'view' | 'edit'`, defaulting
     to view for an existing record and edit for New) and the same thing legacy
     does here — a row click opens Purchaseorder_DetailView READ-ONLY with
     Reports · Roll Back · Edit, and XAF's WebModificationsController.EditAction
     unlocks it (POViewController :87 hooks that action). AI opened it straight
     into edit, so a user who only wanted to look at an order was already in a
     dirty-able form with a Save bar. */
  const [mode, setMode] = useState<'view' | 'edit'>(isNew ? 'edit' : 'view');
  const viewOnly = mode === 'view';
  const [shippingOptions, setShippingOptions] = useState<Option[]>([]);
  const [shipViaOptions, setShipViaOptions] = useState<Option[]>([]);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  // Read-only "Item Requisition" popup (legacy opens the stock Requisition
  // detail view from the Pending Item grid with SaveOnAccept = false).
  const [pendingItem, setPendingItem] = useState<PendingItemDetail | null>(null);
  const [pendingItemOpen, setPendingItemOpen] = useState(false);

  /** Legacy activates the row through XAF's ProcessCurrentObject, which on this
   *  grid means a row click. AI reserves the click for selection, so the popup
   *  opens on DOUBLE-click — which the shared grid also maps to Enter, keeping
   *  it reachable from the keyboard (standing rule 14). */
  const openPendingItem = useCallback(async (row: LinkedRequisition) => {
    setPendingItem(null);
    setPendingItemOpen(true);
    try {
      const res = await api.get(`/api/v1/purchase-orders/pending-item/${row.oid}`);
      setPendingItem(res.data as PendingItemDetail);
    } catch {
      setPendingItemOpen(false);
      notify.error('Failed to open that pending item.');
    }
  }, [notify]);

  /* Loaded once, not per keystroke: the arrows only need the ORDER of ids. */
  const loadSiblings = useCallback(async () => {
    try {
      // No `months`: the arrows walk EVERY order, not the History screen's
      // 3-month default — an arrow that stops at an invisible date boundary
      // reads as a broken button. This endpoint answers with a BARE ARRAY, not
      // the { data } envelope most of the others use.
      const res = await api.get('/api/v1/purchase-orders/history');
      const body = res.data as unknown;
      const rows = (Array.isArray(body)
        ? body
        : ((body as { data?: unknown[] })?.data ?? [])) as Array<{ poOid?: string; oid?: string }>;
      setSiblings(rows.map(r => r.poOid ?? '').filter(Boolean));
    } catch { /* non-fatal: the arrows just stay disabled */ }
  }, []);

  const loadLookups = useCallback(async () => {
    try {
      const [prRes, shRes, svRes, rpRes] = await Promise.all([
        api.get('/api/v1/purchase-orders/pending-requisitions',
          fromVendorId ? { params: { vendorId: fromVendorId } } : undefined),
        api.get('/api/v1/shipping-options'),
        api.get('/api/v1/ship-via'),
        /* The Reports dropdown for THIS view. Legacy targets POReport at both
           Requisition_ListView_Purchaseorder_ViewMode (the History list, which
           AI already wires) and Purchaseorder_DetailView — this one. */
        api.get('/api/v1/purchase-orders/reports', { params: { viewId: 'Purchaseorder_DetailView' } }),
      ]);
      const toArr = (d: unknown): unknown[] => Array.isArray(d) ? d : ((d as { data?: unknown[] })?.data ?? []);
      const mapOpt = (d: unknown) => (toArr(d) as Array<{ oid?: string; id?: string; itemName?: string; name?: string }>)
        .map(i => ({ id: i.oid ?? i.id ?? '', name: i.itemName ?? i.name ?? '' }));
      setPendingReqRows((toArr(prRes.data) as LinkedRequisition[]));
      setReports((toArr(rpRes.data) as Array<{ reportName: string; caption: string }>));
      setShippingOptions(mapOpt(shRes.data));
      setShipViaOptions(mapOpt(svRes.data));

      // Purchase Date + Purchase By are filled on a BLANK order in legacy, not
      // at save time: Purchaseorder.AfterConstruction stamps PurchaseDate and
      // POViewController.Tar_CustomProcessSelectedItem (~714) assigns PurchaseBy.
      // AI left both boxes empty until the PO was saved. Preview only -- the
      // insert still stamps its own GETDATE()/actor, so a form left open for an
      // hour does not freeze a stale timestamp into the record.
      if (isNew) {
        try {
          const d = await api.get('/api/v1/purchase-orders/new-defaults');
          const { purchaseDate, purchaseByName } = d.data as PoInfo;
          setInfo(i => ({ ...i, purchaseDate, purchaseByName }));
        } catch { /* non-fatal: the two boxes just stay blank */ }
      }

      // Legacy shows the Vendor Account panel on a NEW purchase order too -- the
      // vendor is known before anything is saved, because you arrive from the
      // Ordering Items vendor row. AI gated the panel on a SAVED PO, so a new
      // one showed an empty right-hand column.
      if (fromVendorId) {
        try {
          const v = await api.get(`/api/v1/purchase-orders/vendor/${fromVendorId}`);
          const d = v.data as PoInfo;
          setInfo(i => ({ ...i, ...d }));
        } catch { /* non-fatal: the panel just stays blank */ }
      }

      // Arrived from a vendor row: the PO starts with that vendor's ENTIRE
      // pending basket already on it, the first line leading (legacy adds every
      // matching Requisition into Purchaseorder.Item before opening the view).
      if (fromVendorId) {
        const basket: LinkedRequisition[] = (toArr(prRes.data) as Array<{
          oid?: string; rqId?: string; rqid?: string; itemName?: string;
          orderQty?: number; unitPrice?: number; expPrice?: number;
          catalog?: string; description?: string; packUnits?: string;
          departmentName?: string; deliveryPriorityName?: string; projectName?: string;
          status?: number;
        }>).map(r => ({
          oid: r.oid ?? '',
          rqid: r.rqId ?? r.rqid ?? null,
          itemName: r.itemName ?? '',
          orderQty: r.orderQty ?? 0,
          unitPrice: r.unitPrice ?? null,
          catalog: r.catalog ?? null,
          description: r.description ?? null,
          packUnits: r.packUnits ?? null,
          expPrice: r.expPrice ?? null,
          departmentName: r.departmentName ?? null,
          deliveryPriorityName: r.deliveryPriorityName ?? null,
          projectName: r.projectName ?? null,
          // A candidate for ordering is PendingOrdering (2) by definition, so an
          // absent status is that, not "unknown" — it must never read as 5/6 and
          // make a fresh line look un-unlinkable.
          status: r.status ?? 2,
        }));
        setLinkedReqs(basket);
        setInfo(i => ({ ...i, vendorName: fromVendorName }));
        const lead = basket[0];
        if (lead) setForm(f => ({ ...f, leadRequisitionId: lead.oid }));
      }
    } catch {
      notify.error('Failed to load lookup data.');
    }
  }, [notify, isNew, fromVendorId, fromVendorName]);

  const loadRecord = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const res = await api.get(`/api/v1/purchase-orders/${id}`);
      const d = res.data as {
        leadRequisitionId?: string; shippingId?: string; shipViaId?: string;
        shippingCharge?: string; numItems?: number; tax?: string;
        totalPrice?: string; expectDate?: string; referenceNumber?: string; trackingNumber?: string;
        comment?: string; poid?: string; requisitions?: LinkedRequisition[]; approvedDate?: string | null;
      } & PoInfo;
      setForm({
        leadRequisitionId: d.leadRequisitionId ?? '',
        shippingOptionId: d.shippingId ?? '',
        shipViaId: d.shipViaId ?? '',
        shippingCharge: d.shippingCharge ?? '',
        tax: Number(d.tax ?? 0),
        totalPrice: Number(d.totalPrice ?? 0),
        expectDate: d.expectDate ?? '',
        referenceNumber: d.referenceNumber ?? '',
        trackingNumber: d.trackingNumber ?? '',
        comment: d.comment ?? '',
      });
      setPoId(d.poid ?? '');
      setInfo({
        purchaseDate: d.purchaseDate, purchaseByName: d.purchaseByName,
        vendorName: d.vendorName, vendorContact: d.vendorContact, vendorAccount: d.vendorAccount,
        vendorAddress: d.vendorAddress, vendorPhone: d.vendorPhone, vendorEmail: d.vendorEmail,
        numItems: d.numItems,
      });
      setLinkedReqs(d.requisitions ?? []);
    } catch {
      notify.error('Failed to load purchase order.');
    } finally {
      setLoading(false);
    }
  }, [id, isNew, notify]);

  useEffect(() => {
    void loadLookups();
    void loadRecord();
    if (!isNew) void loadSiblings();
  }, [loadLookups, loadRecord, loadSiblings, isNew]);

  function set<K extends keyof PoForm>(k: K, v: PoForm[K]) {
    setForm(f => ({ ...f, [k]: v }));
  }

  /** Legacy Purchaseorder_DetailView offers Save AND Save and Close (both top
   *  and bottom). `close` picks the after-step; the save itself is identical. */
  async function handleSave(close = false) {
    // The lead requisition is no longer a field the operator picks (legacy has
    // no such control — the lead IS the vendor row you arrived from, and the
    // basket sets it). It can only be missing when the page was opened without
    // a vendor, which legacy cannot do, so say what to do rather than 400.
    if (isNew && !form.leadRequisitionId) {
      notify.warning('Open this order from an Ordering Items vendor row — a purchase order needs its pending items.');
      return;
    }
    // Shipping Option and Ship Via carry the red * here and are required in
    // legacy too. The form posted regardless and the server stored nulls, so say
    // which field is missing instead of failing downstream.
    if (!form.shippingOptionId) { notify.error('Shipping Option is required.'); return; }
    if (!form.shipViaId)        { notify.error('Ship Via is required.');        return; }
    // Legacy POViewController.SaveAction_Executing ~1334 rejects a non-numeric
    // Shipping Charge with "Input must contain only numeric value in the field
    // 'Shipping Charge'." The field is free text here, so the same check applies.
    if (form.shippingCharge.trim() !== '' && !/^\d*\.?\d+$/.test(form.shippingCharge.trim())) {
      notify.error("Input must contain only numeric value in the field 'Shipping Charge'."); return;
    }

    /* The PO id the CREATE minted. Legacy announces it —
       POViewController's post-save callback ends with
       "Purchase Order '<POID>' created successfully." on BOTH its branches
       (~2263 after the report download, ~2294 when the report is declined).
       AI discarded the create response entirely, so the id was never shown;
       captured here and read back in successDialog, which useSaveFlow
       evaluates AFTER the save for exactly this reason. */
    let mintedPoid: string | null = null;

    await saveFlow({
      formName: 'ICM Purchase Order',
      auditAction: isNew ? 'create' : 'edit',
      save: async () => {
        // Build the body to the CONTRACT (PurchaseOrderSaveInput) rather than
        // posting form state raw, which is what broke the save outright with a
        // 400 "The JSON value could not be converted" (owner report 2026-09-25:
        // "unable to save the data in the ordering items"). Four separate faults:
        //   · `shippingOptionId` is named `ShippingId` server-side, so a chosen
        //     Shipping Option NEVER bound — it was silently dropped even when
        //     the save otherwise succeeded;
        //   · `shipViaId` / `expectDate` went as "" where the contract has
        //     Guid? / DateTime?, and System.Text.Json cannot convert an empty
        //     string to either — this is the one that returned the 400;
        //   · `tax` / `totalPrice` went as NUMBERS where the contract has
        //     string? (the legacy columns are nvarchar), which also fails.
        // Empty string -> null on every optional field, and the numerics are
        // stringified once, here.
        const total = linkedReqs.reduce((sum, r) => sum + (r.expPrice ?? 0), 0);
        const taxNum  = Number(form.tax) || 0;
        const shipNum = Number(form.shippingCharge) || 0;
        const body = {
          leadRequisitionId: form.leadRequisitionId,
          requisitionIds:    linkedReqs.map(r => r.oid),
          shippingId:        form.shippingOptionId || null,
          shipViaId:         form.shipViaId || null,
          shippingCharge:    form.shippingCharge || null,
          referenceNumber:   form.referenceNumber || null,
          trackingNumber:    form.trackingNumber || null,
          expectDate:        form.expectDate || null,
          // The saved total is the SAME number the Pending Item grid sums, so
          // what the form shows is what is stored.
          totalPrice:        total.toFixed(2),
          /* LEGACY FORMULA (POViewController.SaveAction_Executing ~1362):
               TotalPriceWithTax = TotalPrice + ShippingCharge + (Tax / 100)
             AI computed `total + tax`, DROPPING the shipping charge entirely.
             Verified against live PO26033101: TotalPrice 6 + Shipping 69 is
             stored as 75.0, which only the legacy formula produces. The /100 on
             Tax is legacy's own (it adds tax as an absolute amount, not a
             percentage of the price) and is preserved rather than "corrected" —
             every stored total in the database was computed that way. */
          totalPriceWithTax: total + shipNum + taxNum / 100,
          tax:               String(taxNum),
          comment:           form.comment || null,
        };
        // The basket travels with the create so the PO is saved WITH its lines,
        // rather than saved empty and then linked one call at a time.
        if (isNew) {
          // CreateAsync returns { id, poid } — the minted "PO"+yyMMdd+NN.
          const res = await api.post<{ id: string; poid: string | null }>('/api/v1/purchase-orders', body);
          mintedPoid = res.data?.poid ?? null;
        } else {
          await api.put(`/api/v1/purchase-orders/${id}`, body);
        }
      },
      /* §13 milestone-success MODAL for a NEW order, which is the one case
         §18.6 allows a minted ID to be surfaced ("a milestone-success dialog
         may show the minted ID"); a plain toast may not, so this is not a new
         exception to that rule. Only for a create — an edit keeps the ordinary
         "Saved successfully." */
      successDialog: isNew
        ? () => ({
            type: 'success' as const,
            title: 'Purchase Order Created',
            idLabel: 'PO #',
            idValue: mintedPoid ?? '',
            rows: [
              { label: 'Vendor', value: info.vendorName || '—' },
              { label: 'Items ordered', value: String(linkedReqs.length) },
              { label: 'Total price', value: linkedReqs.reduce((s, r) => s + (r.expPrice ?? 0), 0).toFixed(2) },
            ],
            note: 'Returning to Ordering Items.',
          })
        : undefined,
      /* A NEW order always returns to the list, as legacy does: its callback
         either closes the detail (Save and Close) or switches the frame back to
         Requisition_ListView_Purchaseorder_Mainview (~2291). Staying put was the
         reported bug — for a create there is no id yet, so the old
         `loadRecord()` reloaded nothing and the page just sat there.
         An EDIT keeps the Save-trio standard (rule 12): Save stays on the
         record, Save and Close returns. */
      onSaved: () => {
        if (isNew || close) { navigate('/m/icm-purchase-order'); return; }
        // Save (not Save and Close) stays on the record — and drops back to
        // VIEW, so the record reads the same way it did before Edit unlocked it
        // rather than leaving a saved order sitting in a dirty-able form.
        setMode('view');
        void loadRecord();
      },
    });
  }

  /* LINK / UNLINK — legacy's LinkUnlinkController on the Pending Item grid.
     BOTH ARE BUFFERED: legacy's handlers mutate the in-memory collection
     (POViewController.UnlinkAction_Executing walks POInfo.LinkedPOItems /
     UnLinkedPOItems) and nothing reaches the database until SAVE, which is
     already how AI persists this set — PurchaseOrderSaveInput.RequisitionIds
     carries the whole basket on the PUT.

     ⚠️ The previous combo + Delete pair POSTed to
     /purchase-orders/{id}/link-requisition and /unlink-requisition. NEITHER
     ENDPOINT EXISTS — grep across the whole API returns nothing — so every link
     and every unlink on this form 404'd and then reported "Failed to link
     requisition." No amount of using it would have worked. */

  function linkPicked(ids: string[]) {
    if (ids.length === 0) { setLinkOpen(false); return; }
    const have = new Set(linkedReqs.map(r => r.oid));
    const add = pendingReqRows.filter(r => ids.includes(r.oid) && !have.has(r.oid));
    setLinkedReqs([...linkedReqs, ...add]);
    setLinkOpen(false);
    notify.success(`${add.length} item(s) linked. Save to commit.`);
  }

  function unlinkSelected() {
    if (selected.size === 0) { notify.warning('Select one or more items to unlink.'); return; }

    /* Legacy refuses PER ROW and keeps going (UnlinkAction_Executing :905,
       "Item already received, Cannot be unlinked"), so a mixed selection unlinks
       what it can and names what it could not — it does not abandon the batch. */
    const picked  = linkedReqs.filter(r => selected.has(r.oid));
    const blocked = picked.filter(r => r.status === 5 || r.status === 6);
    const freeing = picked.filter(r => r.status !== 5 && r.status !== 6);

    if (blocked.length > 0) {
      notify.error(`Item already received, Cannot be unlinked: ${
        blocked.map(r => r.rqid ?? r.itemName).join(', ')}`);
    }
    if (freeing.length === 0) return;

    const drop = new Set(freeing.map(r => r.oid));
    setLinkedReqs(linkedReqs.filter(r => !drop.has(r.oid)));
    setSelected(new Set());
    notify.success(`${freeing.length} item(s) unlinked. Save to commit.`);
  }

  /** "Roll Back" — legacy POViewController.POrollback_Execute. The reason is
   *  mandatory; the server refuses the WHOLE order if any line is received. */
  async function rollBack() {
    if (!id || isNew) { notify.warning('Save the purchase order first.'); return; }
    const reason = await prompt({
      title: 'Roll Back',
      message: `Roll back ${poId}? Every item on it returns to Pending Ordering and the order is deleted. `
             + 'Enter the reason for the rollback.',
      placeholder: 'Reason',
      confirmLabel: 'Roll Back',
      required: true,
    });
    if (!reason) return;
    try {
      await api.post(`/api/v1/purchase-orders/${id}/rollback`, { reason });
      notify.success(`${poId} rolled back.`);
      navigate('/m/icm-purchase-order');
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message;
      notify.error(msg || 'Roll back failed.');
    }
  }

  /** "Reports" — legacy POReport_Execute on Purchaseorder_DetailView: renders
   *  the xr_ICMPOmail layout for THIS order and downloads it. The renderer
   *  already exists (the History list uses it), so this is wiring. */
  async function runReport(reportName: string, caption: string) {
    setReportsOpen(false);
    if (!id || isNew) { notify.warning('Save the purchase order first.'); return; }
    if (reportName !== 'xr_ICMPOmail') {
      notify.warning(`"${caption}" is an ALPACA Report Builder report; it is not available in Alpaca Intelligence yet.`);
      return;
    }
    try {
      const res = await api.get(`/api/v1/receive-items/${id}/po-report`, { responseType: 'blob' });
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: 'application/pdf' });
      const disposition = String(res.headers?.['content-disposition'] ?? '');
      const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
      const stamp = new Date().toISOString().replace(/[-:T]/g, '').slice(0, 14);
      const filename = match?.[1] ? decodeURIComponent(match[1].trim()) : `${stamp}.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      notify.success('Report generated.');
    } catch {
      notify.error('Failed to generate the purchase order report.');
    }
  }

  const linkedCols: AppColumn<LinkedRequisition>[] = [
    /* Legacy "Pending Item" grid (Purchaseorder_Item_ListView), in its order:
       Item . Description . Catalog# . PackUnits . OrderQty . UnitPrice .
       ExtPrice . Department . DeliveryPriority . Project, with the Sum summary
       under ExtPrice. Legacy shows no RQID column here. */
    { field: 'itemName',             header: 'Item',             width: 170, text: r => r.itemName },
    { field: 'description',          header: 'Description',      width: 190, text: r => r.description ?? '', render: r => r.description ?? '' },
    { field: 'catalog',              header: 'Catalog#',         width: 120, text: r => r.catalog ?? '', render: r => r.catalog ?? '' },
    { field: 'packUnits',            header: 'PackUnits',        width: 100, text: r => r.packUnits ?? '', render: r => r.packUnits ?? '' },
    { field: 'orderQty',             header: 'OrderQty',         width: 90, type: 'number', text: r => String(r.orderQty), value: r => r.orderQty },
    { field: 'unitPrice',            header: 'UnitPrice',        width: 100, type: 'number', text: r => (r.unitPrice ?? 0).toFixed(2), value: r => r.unitPrice ?? 0 },
    { field: 'expPrice',             header: 'ExtPrice',         width: 100, type: 'number', text: r => (r.expPrice ?? 0).toFixed(2), value: r => r.expPrice ?? 0, summary: 'sum' },
    { field: 'departmentName',       header: 'Department',       width: 120, text: r => r.departmentName ?? '', render: r => r.departmentName ?? '' },
    { field: 'deliveryPriorityName', header: 'DeliveryPriority', width: 130, text: r => r.deliveryPriorityName ?? '', render: r => r.deliveryPriorityName ?? '' },
    { field: 'projectName',          header: 'Project',          width: 130, text: r => r.projectName ?? '', render: r => r.projectName ?? '' },
  ];

  // Amber "required yet empty" cue on a required control (BO UI Standard §4,
  // standing rule 12). Shipping Option / Ship Via are the two fields legacy
  // marks required on Purchaseorder_DetailView.
  /* Read-only in view mode is the SHARED grey cue (standing rule 12's
     "read-only greyed"), not bare text — G5 paints plain labels there, but the
     AI standard form shows the control greyed, and rule 9 says a form inherits
     the shared presentation rather than inventing its own. */
  const fieldCls = viewOnly ? 'sd-input readonly' : 'sd-input';
  const reqCls = (v: unknown) =>
    viewOnly
      ? 'sd-input readonly'
      : 'sd-input' + (v === '' || v == null ? ' required-empty' : '');

  if (loading) return <div className="bl-page"><div className="bl-empty">Loading…</div></div>;

  /* Legacy renders the Save trio at the TOP and again at the BOTTOM of
     Purchaseorder_DetailView, the identical set in both places (standing rule
     12). AI had it at the bottom only, and below the Pending Item grid. */
  const sibIndex = id ? siblings.indexOf(id) : -1;
  function gotoSibling(step: 1 | -1) {
    const next = siblings[sibIndex + step];
    if (next) navigate(`/m/icm-purchase-order/${next}`);
  }

  const renderActions = (where: 'top' | 'bottom') => viewOnly ? null : (
    <div className={`po-actions po-actions--${where}`}>
      <button className="ca-btn" onClick={() => handleSave(false)}>
        <i className="ti ti-device-floppy" /> Save
      </button>
      <button className="ca-btn" onClick={() => handleSave(true)}>
        <i className="ti ti-device-floppy" /> Save and Close
      </button>
      <button className="ca-btn ca-btn-ghost"
              onClick={() => {
                /* From an EXISTING record, Cancel drops back to view and reloads
                   the saved values — it does not throw the user out of the
                   record, which is what legacy's Cancel does too. On a NEW order
                   there is nothing to fall back to, so it returns to the list. */
                if (isNew) { navigate('/m/icm-purchase-order'); return; }
                setMode('view');
                void loadRecord();
              }}>
        Cancel
      </button>
    </div>
  );

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>ICM Operations</span><i className="ti ti-chevron-right" />
            <span onClick={() => navigate('/m/icm-purchase-order')} style={{ cursor: 'pointer' }}>Ordering Items</span>
            <i className="ti ti-chevron-right" />
            <span className="dp-bc-active">{isNew ? 'New PO' : poId}</span>
          </div>
          <div className="dp-title-row">
            <FormTitle pageId="icm-purchase-orders" title={isNew ? 'New Purchase Order' : `Ordering Items — ${poId}`} loadMs={null} loading={false} />
            {/* Prev / next RECORD (standing rule 12; legacy puts them top-right
                of Purchaseorder_DetailView). Disabled at the ends and while the
                sibling list has not arrived, rather than hidden, so the control
                does not appear and disappear under the user. */}
            {!isNew && (
              <span className="po-recnav">
                <button type="button" className="ca-btn ca-btn-ghost"
                        title="Previous purchase order (Alt+←)"
                        disabled={sibIndex <= 0}
                        onClick={() => gotoSibling(-1)}>
                  <i className="ti ti-chevron-left" />
                </button>
                <button type="button" className="ca-btn ca-btn-ghost"
                        title="Next purchase order (Alt+→)"
                        disabled={sibIndex < 0 || sibIndex >= siblings.length - 1}
                        onClick={() => gotoSibling(1)}>
                  <i className="ti ti-chevron-right" />
                </button>
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="bl-grid-area">
        <div style={{ margin: '0 0 8px' }}>
          {/* Legacy's detail toolbar is Reports ▾ · Roll Back (POViewController
              :63 POrollback and :68 POReport both target Purchaseorder_DetailView).
              Refresh stays — it is the shared standard every form carries. */}
          <AppToolbar
            left={!isNew ? (
              <>
                {reports.length > 0 && (
                  <span className="po-reports-wrap">
                    <button type="button" className="ca-btn" onClick={() => setReportsOpen(o => !o)}>
                      <i className="ti ti-file-text" /> Reports <i className="ti ti-chevron-down" />
                    </button>
                    {reportsOpen && (
                      <>
                        <div className="po-reports-backdrop" onClick={() => setReportsOpen(false)} />
                        <div className="po-reports-menu" role="menu">
                          {reports.map(r => (
                            <button key={r.reportName} type="button" role="menuitem"
                              className="po-reports-item"
                              onClick={() => void runReport(r.reportName, r.caption)}>
                              {r.caption}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </span>
                )}
                <button type="button" className="ca-btn" onClick={() => void rollBack()}>
                  <i className="ti ti-arrow-back-up" /> Roll Back
                </button>
                {/* Legacy's view-mode bar is Reports · Roll Back · Edit; Edit is
                    XAF's WebModificationsController action, which unlocks THIS
                    detail rather than opening a second one. */}
                {viewOnly && (
                  <button type="button" className="ca-btn" onClick={() => setMode('edit')}>
                    <i className="ti ti-edit" /> Edit
                  </button>
                )}
              </>
            ) : undefined}
            onRefresh={loadRecord}
            refreshing={loading}
          />
        </div>

        <div className="bl-card po-detail-card" style={{ padding: 20 }}>
          {renderActions('top')}

          {/* TWO PANELS SIDE BY SIDE, as legacy draws Purchaseorder_DetailView —
              the order's own fields on the LEFT, "Vendor Account" on the RIGHT,
              each a single stacked column. AI had one 2-column grid across the
              full width with Vendor Account as a section underneath, which read
              as one long form instead of the two panels the operator knows.
              Field order is legacy's, column by column (Model.xafml
              Purchaseorder_col1 / the "Vendor Account" group):
                col 1  Purchase Date . Purchase By . Reference Number .
                       Shipping Option* . Ship Via* . Shipping Charge .
                       Tracking Number . Num Item . Tax . Total Price
                col 2  Vendor . Contact . Account . Address . Phone . Email .
                       Expect Date . Comment
              Purchase Date, Purchase By, Num Item and Total Price are
              server-owned and read-only, as in legacy. */}
          <div className="po-panels">
          <div className="po-panel">
          {/* Legacy gives the left panel no caption; the spacer keeps both
              panels' first field on the same line. */}
          <div className="po-panel-h" />
          <div className="sd-grid sd-grid--single sd-grid--side">
            <label className="sd-row">
              <span className="sd-label">Purchase Date</span>
              <input className="sd-input readonly" readOnly
                     value={info.purchaseDate ? fmtLocalDateTime(info.purchaseDate) ?? '' : ''} />
            </label>

            <label className="sd-row">
              <span className="sd-label">Purchase By</span>
              <input className="sd-input readonly" readOnly value={info.purchaseByName ?? ''} />
            </label>

            <label className="sd-row">
              <span className="sd-label">Reference Number</span>
              <input className={fieldCls} readOnly={viewOnly} type="text" value={form.referenceNumber} onChange={e => set('referenceNumber', e.target.value)} />
            </label>

            <label className="sd-row">
              <span className="sd-label">Shipping Option <span className="req">*</span></span>
              <select className={reqCls(form.shippingOptionId)} disabled={viewOnly} value={form.shippingOptionId} onChange={e => set('shippingOptionId', e.target.value)}>
                <option value="">— select —</option>
                {shippingOptions.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </label>

            {/* Ship Via is an FK (dbo.Purchaseorder.ShipVia -> dbo.ShipVia). It
                was a free-text box, so a typed value could never be saved. */}
            <label className="sd-row">
              <span className="sd-label">Ship Via <span className="req">*</span></span>
              <select className={reqCls(form.shipViaId)} disabled={viewOnly} value={form.shipViaId} onChange={e => set('shipViaId', e.target.value)}>
                <option value="">— select —</option>
                {shipViaOptions.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </label>

            <label className="sd-row">
              <span className="sd-label">Shipping Charge</span>
              <input className={fieldCls} readOnly={viewOnly} type="text" value={form.shippingCharge} onChange={e => set('shippingCharge', e.target.value)} />
            </label>

            <label className="sd-row">
              <span className="sd-label">Tracking Number</span>
              <input className={fieldCls} readOnly={viewOnly} type="text" value={form.trackingNumber} onChange={e => set('trackingNumber', e.target.value)} />
            </label>

            <label className="sd-row">
              <span className="sd-label">Num Item</span>
              <input className="sd-input readonly" readOnly value={info.numItems ?? linkedReqs.length} />
            </label>

            <label className="sd-row">
              <span className="sd-label">Tax</span>
              <input className={fieldCls} readOnly={viewOnly} type="number" min={0} step="0.01" value={form.tax} onChange={e => set('tax', Number(e.target.value))} />
            </label>

            <label className="sd-row">
              <span className="sd-label">Total Price</span>
              <input className="sd-input readonly" readOnly value={form.totalPrice.toFixed(2)} />
            </label>

            {/* NOTE: legacy has NO "Lead Requisition" control — the lead IS the
                vendor row you opened the order from, and the whole pending
                basket comes with it. AI showed a picker here, which is a field
                the operator has no way to answer. It is now derived (the first
                basket line) and kept in form state, not rendered. */}
          </div>
          </div>

          {/* Vendor Account — legacy Purchaseorder_DetailView's right-hand
              column, and a panel of its own rather than a section below the
              form. Shown on a NEW purchase order too: the vendor is known
              before anything is saved, because you arrive from the Ordering
              Items vendor row. The five contact fields live on dbo.Vendors
              (dbo.Purchaseorder has no such columns), so they are display-only,
              exactly as in legacy. Expect Date and Comment belong to this
              column in the legacy layout and ARE editable. */}
          <div className="po-panel">
          <div className="po-panel-h">Vendor Account</div>
          <div className="sd-grid sd-grid--single sd-grid--side">
            {([
              ['Vendor',  info.vendorName],
              ['Contact', info.vendorContact],
              ['Account', info.vendorAccount],
              ['Address', info.vendorAddress],
              ['Phone',   info.vendorPhone],
              ['Email',   info.vendorEmail],
            ] as Array<[string, string | null | undefined]>).map(([label, value]) => (
              <div className="sd-row" key={label}>
                <span className="sd-label">{label}</span>
                <input className="sd-input readonly" readOnly value={value ?? ''} />
              </div>
            ))}

            <label className="sd-row">
              <span className="sd-label">Expect Date</span>
              <input className={fieldCls} readOnly={viewOnly} type="date" value={form.expectDate} onChange={e => set('expectDate', e.target.value)} />
            </label>

            {/* rows={1}: the shared CSS pins every control to 26px, so a taller
                `rows` only fights it — and if that rule is ever overridden the
                field would silently grow past its row. Still drag-resizable. */}
            <label className="sd-row sd-row--grow">
              <span className="sd-label">Comment</span>
              <textarea className={fieldCls} readOnly={viewOnly} rows={1} value={form.comment} onChange={e => set('comment', e.target.value)} />
            </label>
          </div>
          </div>
          </div>

          {/* "Pending Item" — the order's lines, BELOW both panels and ABOVE the
              bottom action bar, which is where legacy puts it. Its toolbar is
              "Export to" alone on a NEW order (legacy shows nothing else there);
              the link / unlink actions belong to a SAVED order, where legacy's
              LinkUnlinkController supplies them, so they appear only then. */}
          <div style={{ marginTop: 24 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Pending Item</div>
            {/* Legacy's grid bar is Link · Unlink · Export to (the shared
                LinkUnlinkController), not a picker combo and a Delete button. */}
            <AppToolbar
              left={!isNew && !viewOnly ? (
                <>
                  <button type="button" className="ca-btn"
                          onClick={() => { setLinkPick(new Set()); setLinkOpen(true); }}>
                    <i className="ti ti-link" /> Link
                  </button>
                  <button type="button" className="ca-btn"
                          disabled={selected.size === 0}
                          onClick={unlinkSelected}>
                    <i className="ti ti-unlink" /> Unlink
                  </button>
                </>
              ) : undefined}
              exportData={{
                filename: `PendingItem-${poId || 'new'}`,
                ...gridExportMatrix(linkedReqs, linkedCols),
              }}
            />
            <div className="bl-card bl-card-flex" style={{ marginTop: 8 }}>
              <AppDataGrid<LinkedRequisition>
                rows={linkedReqs}
                columns={linkedCols}
                getRowId={r => r.oid}
                loading={loading}
                loadMs={null}
                selectable
                selectedIds={selected}
                onSelectionChange={setSelected}
                onRowDoubleClick={openPendingItem}
                emptyText={isNew
                  ? 'No pending items. Open this order from an Ordering Items vendor row.'
                  : 'No requisitions linked. Select one above to link.'}
              />
            </div>
          </div>

          {renderActions('bottom')}
        </div>
      </div>

      {/* Legacy "Item Requisition" popup. Read-only: its DialogController runs
          with SaveOnAccept = false, so OK simply closes. Field order is the
          live Requisition_DetailView layout — left column then right. */}
      <AppDialog
        open={pendingItemOpen}
        title="Item Requisition"
        icon="ti-clipboard-list"
        width={760}
        onClose={() => setPendingItemOpen(false)}
        footer={<button className="ca-btn" onClick={() => setPendingItemOpen(false)}>OK</button>}
      >
        {!pendingItem ? (
          <div className="bl-empty">Loading…</div>
        ) : (
          <>
            <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 12 }}>Requisition</div>
            <div className="po-pi-grid">
              <div>
                <PiField label="Item"            value={pendingItem.itemName} to="set-inventory-items" />
                <PiField label="Category"        value={pendingItem.categoryName} />
                <PiField label="Vendor"          value={pendingItem.vendorName} to="set-vendors" />
                <PiField label="Description"     value={pendingItem.description} />
                <PiField label="PackUnits"       value={pendingItem.packUnits} to="set-package-units" />
                <PiField label="Item Unit"       value={pendingItem.itemUnit == null ? null : String(pendingItem.itemUnit)} />
                <PiField label="Catalog#"        value={pendingItem.catalog} />
                <PiField label="Name"            value={pendingItem.nameText} />
                <PiField label="Approved By"     value={pendingItem.approvedByName} to="sec-users" />
              </div>
              <div>
                <PiField label="Stock Qty"       value={String(pendingItem.stockQty)} />
                <PiField label="Order Qty"       value={String(pendingItem.orderQty)} />
                <PiField label="Unit Price"      value={pendingItem.unitPrice.toFixed(2)} />
                <PiField label="Ext Price"       value={pendingItem.extPrice.toFixed(2)} />
                <PiField label="Shipping Option" value={pendingItem.shippingOption} to="set-shipping-options" />
                <PiField label="Comment"         value={pendingItem.comment} />
                <PiField label="Total Price"     value={pendingItem.totalPrice.toFixed(2)} />
                <PiField label="Delivery Priority" value={pendingItem.deliveryPriorityName} to="set-delivery-priority" />
                <PiField label="Project"         value={pendingItem.projectName} to="set-icm-project" />
                <PiField label="Division"        value={pendingItem.divisionName} to="sm-division" />
              </div>
            </div>
          </>
        )}
      </AppDialog>

      {/* LINK picker — legacy's LinkUnlinkController opens the standard XAF
          lookup list of candidates; here that is the same
          /purchase-orders/pending-requisitions feed the form already loads,
          minus whatever is already on the order. Multi-select + OK, buffered
          into the grid until Save, exactly as legacy buffers it. */}
      <AppDialog
        open={linkOpen}
        title="Link Pending Item"
        icon="ti-link"
        width={900}
        onClose={() => setLinkOpen(false)}
        footer={
          <>
            <button className="ca-btn" disabled={linkPick.size === 0}
                    onClick={() => linkPicked(Array.from(linkPick) as string[])}>
              OK
            </button>
            <button className="ca-btn ca-btn-ghost" onClick={() => setLinkOpen(false)}>Cancel</button>
          </>
        }
      >
        <AppDataGrid<LinkedRequisition>
          rows={pendingReqRows.filter(r => !linkedReqs.some(l => l.oid === r.oid))}
          columns={linkedCols}
          getRowId={r => r.oid}
          loading={false}
          loadMs={null}
          selectable
          selectedIds={linkPick}
          onSelectionChange={setLinkPick}
          emptyText="No pending requisitions are waiting to be ordered for this vendor."
        />
      </AppDialog>
    </div>
  );
}

/** One read-only label/value pair in the Pending Item popup. Legacy renders an
 *  empty value as "N/A", not a blank, so the two columns stay aligned.
 *
 *  `to` marks the rows GoLive draws as HYPERLINKS. XAF renders a REFERENCE
 *  property as a link to its form, and these are references on the Requisition
 *  BO: `Item` → `Items`, `Vendor` → `Vendors`, `ShippingOption`, `ApprovedBy` →
 *  `Employee`, `DeliveryPriority`, `Project` → `ICMProject`, `Division` →
 *  `Company`, plus `PackUnits` (`Item.Unit` → `Packageunits`). AI flattens them
 *  to display names, so the link is restored explicitly through <FormLink>.
 *
 *  ONE APPROXIMATE TARGET: legacy's `ApprovedBy` is an HR `Employee`, and AI has
 *  no Employee form — `sec-users` (User & Security → Users) is the only screen
 *  that lists people, so that is where it points. It is permission-gated like
 *  every other target, so a user without User & Security simply reads the name.
 *
 *  WHAT IS DELIBERATELY *NOT* LINKED — the rows bound to a STRING drill-down
 *  rather than a reference, which XAF draws as a plain label: **Category** is
 *  `Item.Category.category` and **Name** is that same string again
 *  (Model.DesignedDiffs.xafml:3842, Requisition_DetailView). Category carried a
 *  link for one build on 2026-09-29 and was corrected the same day — it reads as
 *  a lookup, but legacy shows it black, and this popup follows legacy.
 *  Description / Item Unit / Catalog# / Specification / Grade / the quantities
 *  and prices / Comment are plain for the same reason.
 *
 *  An EMPTY value stays plain text too: GoLive does draw a null reference as a
 *  link (its null-text "N/A" sits inside the hyperlink editor, so Approved By /
 *  Shipping Option / Project look like links when unset), but clicking one there
 *  navigates nowhere. Offering a link to a record that does not exist would be
 *  worse than the placeholder it replaces. */
function PiField({ label, value, to }: { label: string; value: string | null | undefined; to?: string }) {
  return (
    <div className="po-pi-row">
      <span className="po-pi-label">{label}:</span>
      {to
        ? <FormLink to={to} value={value} className="po-pi-value" />
        : <span className="po-pi-value">{value == null || value === '' ? 'N/A' : value}</span>}
    </div>
  );
}
